/**
 * Chrome-like tab strip for the boards on `/dashboard`. Click switches, double-click renames
 * inline, `+` appends a board, and each tab's `x` asks the parent to confirm before deleting —
 * deleting a board deletes its pins, so it isn't undoable. The `x` is hidden when only one board
 * remains, since the backend refuses to delete the last one anyway.
 */

import { Plus, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { Button } from "@/components/ui/button";
import type { DashboardSummary } from "@/lib/api";
import { cn } from "@/lib/utils";

interface DashboardTabsProps {
  dashboards: DashboardSummary[];
  activeId: string;
  onSelect: (id: string) => void;
  onCreate: () => void;
  creating: boolean;
  onRename: (id: string, name: string) => void;
  onDeleteRequest: (id: string) => void;
}

export function DashboardTabs({
  dashboards,
  activeId,
  onSelect,
  onCreate,
  creating,
  onRename,
  onDeleteRequest,
}: DashboardTabsProps) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingValue, setEditingValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (editingId) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editingId]);

  function startRename(dashboard: DashboardSummary) {
    setEditingId(dashboard.id);
    setEditingValue(dashboard.name);
  }

  function commitRename() {
    const id = editingId;
    setEditingId(null);
    if (!id) {
      return;
    }
    const trimmed = editingValue.trim();
    const current = dashboards.find((d) => d.id === id);
    if (trimmed && trimmed !== current?.name) {
      onRename(id, trimmed);
    }
  }

  return (
    <div role="tablist" aria-label="Dashboards" className="flex items-end gap-1 overflow-x-auto">
      {dashboards.map((dashboard) => {
        const active = dashboard.id === activeId;
        const editing = editingId === dashboard.id;
        return (
          <div
            key={dashboard.id}
            role="tab"
            aria-selected={active}
            className={cn(
              "group relative flex shrink-0 items-center gap-1.5 rounded-t-lg border border-b-0 px-3 py-1.5 text-sm transition-colors",
              active
                ? "border-border bg-card text-foreground font-medium"
                : "text-muted-foreground hover:bg-card/60 hover:text-foreground border-transparent",
            )}
          >
            {editing ? (
              <input
                ref={inputRef}
                value={editingValue}
                onChange={(e) => setEditingValue(e.target.value)}
                onBlur={commitRename}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    commitRename();
                  } else if (e.key === "Escape") {
                    setEditingId(null);
                  }
                }}
                className="bg-background border-border w-28 rounded border px-1.5 py-0.5 text-sm outline-none"
              />
            ) : (
              <button
                type="button"
                onClick={() => onSelect(dashboard.id)}
                onDoubleClick={() => startRename(dashboard)}
                title={`${dashboard.name} (double-click to rename)`}
                className="max-w-40 truncate"
              >
                {dashboard.name}
              </button>
            )}
            {!editing && dashboards.length > 1 ? (
              <button
                type="button"
                aria-label={`Delete ${dashboard.name}`}
                title="Delete board"
                onClick={() => onDeleteRequest(dashboard.id)}
                className="text-muted-foreground hover:text-destructive shrink-0 rounded-full p-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
              >
                <X className="h-3 w-3" />
              </button>
            ) : null}
          </div>
        );
      })}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        aria-label="New dashboard"
        title="New dashboard"
        disabled={creating}
        onClick={onCreate}
        className="shrink-0 rounded-t-lg rounded-b-none px-2"
      >
        <Plus className="h-4 w-4" />
      </Button>
    </div>
  );
}
